import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Users, Search, Filter, Plus, Edit, Calendar, Package, Trash2, ArrowLeft, ChevronLeft, ChevronRight, Sparkles, Mail } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import {
  assignUserPackage,
  deletePreference,
  fetchAllPreferences,
  fetchPackages,
  fetchUserPackages,
  seedDefaultPackages,
  updateUserPackage,
  upsertPreference,
  websiteUsersWithoutPreferences,
} from '../../lib/instantData'
import {
  addTerm,
  formatTermLength,
  termsForPackage,
  PackageTerm,
} from '../../lib/packageCatalog'
import { brandAlert, brandConfirm } from '../../contexts/DialogContext'
import { isAdminEmail } from '../../lib/adminEmails'
import { db } from '../../lib/db'
import GlassCard from '../../components/ui/GlassCard'
import PanelShell from '../../components/ui/PanelShell'
import { useAuth } from '../../contexts/AuthContext'
import DatePicker from '../../components/ui/DatePicker'

interface Client {
  id: string
  email: string
  full_name?: string
  created_at: string
  avatar_url?: string
  last_login?: string
  phone?: string
  location?: string
  date_of_birth?: string
  fitness_goals?: string[]
  workout_time?: string
}

interface ClientPackage {
  id: string
  package_name: string
  status: string
  purchase_date: string
  expiry_date: string
  sessions_remaining?: number
  tracker_url?: string
  tracker_type?: string
  includes_training?: boolean
  competition_prep?: boolean
}

interface UserPackageData {
  id: string
  package_id: string
  user_id: string
  status: string
  purchase_date: string
  expiry_date: string
  sessions_remaining?: number
  packages?: {
    name: string
    package_type: string
  }
}

const CUSTOM_TERM = -1

const PACKAGE_GROUPS: { type: string; label: string }[] = [
  { type: 'in_person', label: 'Private Training' },
  { type: 'online', label: 'Online Training' },
  { type: 'meal_plan', label: 'Meal Plans' },
]

/** Reads a `YYYY-MM-DD` value as a local date, so the day doesn't shift by timezone. */
const parseDateInput = (value: string): Date | null => {
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

const AdminClients: React.FC = () => {
  const { adminUser } = useAdminAuth()
  const { user } = useAuth()
  const { user: instantUser } = db.useAuth()
  const guideToken = instantUser?.refresh_token
  const navigate = useNavigate()
  const [sendingGuideFor, setSendingGuideFor] = useState<string | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [clientPackages, setClientPackages] = useState<ClientPackage[]>([])
  const [trackerEdits, setTrackerEdits] = useState<Record<string, {
    trackerUrl: string
    trackerType: string
    includesTraining: boolean
    competitionPrep: boolean
  }>>({})
  const [savingTrackerFor, setSavingTrackerFor] = useState<string | null>(null)
  const [sendingCompPrepFor, setSendingCompPrepFor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [showAddClientModal, setShowAddClientModal] = useState(false)
  const [websiteUsers, setWebsiteUsers] = useState<any[]>([])
  const [showUsersSection, setShowUsersSection] = useState(false)
  const [showAssignPackageModal, setShowAssignPackageModal] = useState(false)
  const [availablePackages, setAvailablePackages] = useState<any[]>([])
  const [packageAssignmentDate, setPackageAssignmentDate] = useState(new Date().toISOString().split('T')[0])
  const [showEditModal, setShowEditModal] = useState(false)
  // Assign-package flow: step 1 picks the program, step 2 sets its length and sessions
  const [assignStep, setAssignStep] = useState<1 | 2>(1)
  const [assignPackageId, setAssignPackageId] = useState('')
  const [assignTermIndex, setAssignTermIndex] = useState(0)
  const [assignCustomLength, setAssignCustomLength] = useState('12')
  const [assignCustomUnit, setAssignCustomUnit] = useState<'week' | 'month'>('week')
  const [assignSessions, setAssignSessions] = useState('')
  const [assigningPackage, setAssigningPackage] = useState(false)
  const [seedingPackages, setSeedingPackages] = useState(false)
  // Form state for adding new client
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    location: '',
    date_of_birth: '',
    fitness_goals: [] as string[],
    workout_time: ''
  })

  useEffect(() => {
    fetchClients()
    fetchWebsiteUsers()
    fetchAvailablePackages()
  }, [])

  const fetchAvailablePackages = async () => {
    try {
      const data = await fetchPackages(true)
      setAvailablePackages(data)
    } catch (error) {
      console.error('Error fetching packages:', error)
    }
  }

  const openAssignPackageModal = () => {
    setAssignStep(1)
    setAssignPackageId('')
    setAssignTermIndex(0)
    setAssignCustomLength('12')
    setAssignCustomUnit('week')
    setAssignSessions('')
    setPackageAssignmentDate(new Date().toISOString().split('T')[0])
    setShowAssignPackageModal(true)
  }

  const chooseAssignPackage = (pkg: any) => {
    setAssignPackageId(pkg.id)
    setAssignTermIndex(0)
    setAssignSessions(pkg.sessions_included ? String(pkg.sessions_included) : '')
    setAssignStep(2)
  }

  const addWebsitePackages = async () => {
    setSeedingPackages(true)
    try {
      const created = await seedDefaultPackages()
      await fetchAvailablePackages()
      if (created === 0) {
        void brandAlert({
          title: 'Nothing to add',
          message:
            'Your programs already exist but none are active. Turn them back on under Package Management.',
          variant: 'info',
        })
      }
    } catch (error) {
      console.error('Error adding website packages:', error)
      void brandAlert({
        title: 'Something went wrong',
        message: 'Could not add your website programs. Please try again.',
        variant: 'error',
      })
    } finally {
      setSeedingPackages(false)
    }
  }

  const assignPackage = availablePackages.find((pkg) => pkg.id === assignPackageId)
  const assignTerms: PackageTerm[] = assignPackage ? termsForPackage(assignPackage) : []
  const customTerm: PackageTerm = {
    label: 'Custom',
    value: parseInt(assignCustomLength, 10) || 0,
    unit: assignCustomUnit,
  }
  const activeTerm: PackageTerm | undefined =
    assignTermIndex === CUSTOM_TERM ? customTerm : assignTerms[assignTermIndex]
  const assignStartDate = packageAssignmentDate ? parseDateInput(packageAssignmentDate) : null
  const assignEndDate =
    assignStartDate && activeTerm && activeTerm.value > 0
      ? addTerm(assignStartDate, activeTerm)
      : null

  const handleAssignPackage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedClient || !assignPackage) return

    if (!assignStartDate) {
      void brandAlert({ title: 'Almost there', message: 'Please pick a start date.', variant: 'error' })
      return
    }
    if (!activeTerm || activeTerm.value <= 0 || !assignEndDate) {
      void brandAlert({
        title: 'Almost there',
        message: 'Please choose how long this package runs.',
        variant: 'error',
      })
      return
    }

    const trimmedSessions = assignSessions.trim()
    const sessions = trimmedSessions === '' ? undefined : parseInt(trimmedSessions, 10)
    if (sessions !== undefined && (Number.isNaN(sessions) || sessions < 0)) {
      void brandAlert({
        title: 'Almost there',
        message: 'Sessions must be a whole number, or left blank.',
        variant: 'error',
      })
      return
    }

    setAssigningPackage(true)
    try {
      await assignUserPackage({
        userId: selectedClient.id,
        packageId: assignPackage.id,
        packageName: assignPackage.name,
        packageType: assignPackage.package_type,
        status: 'active',
        purchaseDate: assignStartDate.getTime(),
        expiryDate: assignEndDate.getTime(),
        sessionsRemaining: sessions,
      })

      fetchClientPackages(selectedClient.id)
      setShowAssignPackageModal(false)
      void brandAlert({
        title: 'Done',
        message: `${assignPackage.name} assigned to ${selectedClient.full_name || selectedClient.email} through ${assignEndDate.toLocaleDateString()}.`,
        variant: 'success',
      })
    } catch (error: any) {
      console.error('Error assigning package:', error)
      void brandAlert({
        title: 'Something went wrong',
        message: 'Failed to assign package: ' + (error?.message || 'Unexpected error'),
        variant: 'error',
      })
    } finally {
      setAssigningPackage(false)
    }
  }

  const fetchClients = async () => {
    try {
      const preferences = await fetchAllPreferences()
      const clientsData: Client[] = preferences
        .filter((pref) => !isAdminEmail(pref.email))
        .map((pref) => ({
          id: pref.user_id,
          email: pref.email || 'unknown@email.com',
          full_name: pref.full_name,
          created_at: pref.created_at || new Date().toISOString(),
          avatar_url: pref.avatar_url,
          last_login: pref.last_login,
          phone: pref.phone,
          location: pref.location,
          date_of_birth: pref.date_of_birth,
          fitness_goals: pref.fitness_goals || [],
          workout_time: pref.workout_time,
        }))
      setClients(clientsData)
    } catch (error) {
      console.error('Error fetching clients:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchWebsiteUsers = async () => {
    try {
      const websiteUsersData = await websiteUsersWithoutPreferences()
      setWebsiteUsers(websiteUsersData)
    } catch (error) {
      console.error('Error fetching website users:', error)
      setWebsiteUsers([])
    }
  }

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!guideToken) {
      void brandAlert({
        title: 'Something went wrong',
        message: 'Your session is still loading — please try again in a moment.',
        variant: 'error',
      })
      return
    }

    try {
      // Creating another person's account genuinely needs elevated
      // privilege ($users is admin-only by permission rule), so this one
      // call goes through a verified-admin server endpoint instead of the
      // browser-side admin SDK. It returns only the new userId, never a
      // token.
      const res = await fetch('/api/admin/ensure-client-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${guideToken}`,
        },
        body: JSON.stringify({ email: formData.email.trim().toLowerCase() }),
      })
      const result = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(result?.error || 'Failed to create the client account.')
      const userId = result.userId

      await upsertPreference(userId, {
        email: formData.email.trim().toLowerCase(),
        fullName: formData.full_name,
        phone: formData.phone || undefined,
        location: formData.location || undefined,
        dateOfBirth:
          formData.date_of_birth && formData.date_of_birth.trim() !== ''
            ? formData.date_of_birth
            : undefined,
        fitnessGoals: formData.fitness_goals,
        workoutTime: formData.workout_time || undefined,
      })

      const newClient: Client = {
        id: userId,
        email: formData.email,
        full_name: formData.full_name,
        created_at: new Date().toISOString(),
        phone: formData.phone,
        location: formData.location,
        date_of_birth: formData.date_of_birth,
        fitness_goals: formData.fitness_goals || [],
        workout_time: formData.workout_time,
      }

      setClients([newClient, ...clients])
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        location: '',
        date_of_birth: '',
        fitness_goals: [] as string[],
        workout_time: '',
      })
      setShowAddClientModal(false)
      void brandAlert({ title: 'Done', message: 'Client added successfully!', variant: 'success' })
    } catch (error: any) {
      console.error('Error adding client:', error)
      void brandAlert({ title: 'Something went wrong', message: 'Error adding client: ' + (error?.message || 'Please try again.'), variant: 'error' })
    }
  }

  const promoteUserToClient = async (webUser: any) => {
    try {
      await upsertPreference(webUser.id, {
        email: webUser.email,
        fullName: webUser.full_name || 'Client',
      })

      const newClient: Client = {
        id: webUser.id,
        email: webUser.email,
        full_name: webUser.full_name,
        created_at: new Date().toISOString(),
      }

      setClients([newClient, ...clients])
      setWebsiteUsers(websiteUsers.filter((u) => u.id !== webUser.id))
      void brandAlert({ title: 'Done', message: 'User promoted to client successfully!', variant: 'success' })
    } catch (error: any) {
      console.error('Error promoting user:', error)
      void brandAlert({ title: 'Something went wrong', message: 'Error promoting user: ' + (error?.message || 'Please try again.'), variant: 'error' })
    }
  }

  const fetchClientPackages = async (clientId: string) => {
    try {
      const data = await fetchUserPackages(clientId)
      const formattedPackages = data.map((pkg: UserPackageData) => ({
        id: pkg.id,
        package_name: pkg.packages?.name || 'Unknown Package',
        status: pkg.status,
        purchase_date: pkg.purchase_date,
        expiry_date: pkg.expiry_date,
        sessions_remaining: pkg.sessions_remaining,
        tracker_url: (pkg as any).tracker_url,
        tracker_type: (pkg as any).tracker_type,
        includes_training: (pkg as any).includes_training,
        competition_prep: (pkg as any).competition_prep,
      }))
      setClientPackages(formattedPackages)
      setTrackerEdits(
        Object.fromEntries(
          formattedPackages.map((pkg) => [
            pkg.id,
            {
              trackerUrl: pkg.tracker_url || '',
              trackerType: pkg.tracker_type || 'standard',
              includesTraining: !!pkg.includes_training,
              competitionPrep: !!pkg.competition_prep,
            },
          ])
        )
      )
    } catch (error) {
      console.error('Error fetching client packages:', error)
    }
  }

  const handleSaveTracker = async (packageId: string) => {
    const edit = trackerEdits[packageId]
    if (!edit) return
    try {
      setSavingTrackerFor(packageId)
      await updateUserPackage(packageId, {
        trackerUrl: edit.trackerUrl.trim(),
        trackerType: edit.trackerType,
        includesTraining: edit.includesTraining,
        competitionPrep: edit.competitionPrep,
      })
      setClientPackages((prev) =>
        prev.map((pkg) =>
          pkg.id === packageId
            ? {
                ...pkg,
                tracker_url: edit.trackerUrl.trim(),
                tracker_type: edit.trackerType,
                includes_training: edit.includesTraining,
                competition_prep: edit.competitionPrep,
              }
            : pkg
        )
      )
      void brandAlert({ title: 'Saved', message: 'Tracker and guide access updated.', variant: 'success' })
    } catch (error: any) {
      console.error('Error saving tracker/entitlements:', error)
      void brandAlert({
        title: 'Something went wrong',
        message: 'Error saving tracker and guide access: ' + (error?.message || 'Please try again.'),
        variant: 'error',
      })
    } finally {
      setSavingTrackerFor(null)
    }
  }

  const handleSendCompetitionPrep = async (packageId: string) => {
    if (!guideToken || !selectedClient) return
    try {
      setSendingCompPrepFor(packageId)
      const res = await fetch('/api/admin/send-guide', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${guideToken}`,
        },
        body: JSON.stringify({
          type: 'competition-prep',
          userId: selectedClient.id,
          email: selectedClient.email,
          firstName: selectedClient.full_name?.split(' ')[0],
        }),
      })
      const result = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(result?.error || 'Failed to send guide.')
      await brandAlert({
        title: 'Guide sent',
        message: `The Competition Prep Guide was emailed to ${selectedClient.email}.`,
      })
    } catch (err: any) {
      console.error('Failed to send competition prep guide:', err)
      await brandAlert({
        title: 'Could not send guide',
        message: err?.message || 'Something went wrong sending the guide. Please try again.',
        variant: 'danger',
      })
    } finally {
      setSendingCompPrepFor(null)
    }
  }

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client)
    fetchClientPackages(client.id)
  }



  const handleEditClient = (e: React.MouseEvent, client: Client) => {
    e.stopPropagation()
    setSelectedClient(client)
    setFormData({
      full_name: client.full_name || '',
      email: client.email,
      phone: client.phone || '',
      location: client.location || '',
      date_of_birth: client.date_of_birth || '',
      fitness_goals: client.fitness_goals || [],
      workout_time: client.workout_time || ''
    })
    setShowEditModal(true)
  }

  const handleSendGuide = async (e: React.MouseEvent, client: Client) => {
    e.stopPropagation()
    if (!guideToken) return
    setSendingGuideFor(client.id)
    try {
      const res = await fetch('/api/admin/send-guide', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${guideToken}`,
        },
        body: JSON.stringify({
          type: 'onboarding',
          email: client.email,
          firstName: client.full_name?.split(' ')[0],
        }),
      })
      const result = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(result?.error || 'Failed to send guide.')
      await brandAlert({
        title: 'Guide sent',
        message: `The Client Welcome Guide was emailed to ${client.email}.`,
      })
    } catch (err) {
      console.error('Failed to send client guide:', err)
      await brandAlert({
        title: 'Could not send guide',
        message: 'Something went wrong sending the guide. Please try again.',
        variant: 'danger',
      })
    } finally {
      setSendingGuideFor(null)
    }
  }

  const handleDeleteClient = async (e: React.MouseEvent, client: Client) => {
    e.stopPropagation()
    const ok = await brandConfirm({
      title: 'Delete client?',
      message: `Are you sure you want to delete ${client.full_name || client.email}? This cannot be undone and will remove their dashboard data.`,
      variant: 'danger',
      confirmLabel: 'Delete client',
      cancelLabel: 'Keep client',
    })
    if (!ok) return

    try {
      const prefs = await fetchAllPreferences()
      const pref = prefs.find((p) => p.user_id === client.id)
      if (pref) {
        await deletePreference(pref.id)
      }

      setClients((prev) => prev.filter((c) => c.id !== client.id))

      if (selectedClient?.id === client.id) {
        setSelectedClient(null)
      }

      void brandAlert({ title: 'Done', message: 'Client deleted successfully!', variant: 'success' })
    } catch (error) {
      console.error('Error deleting client:', error)
      void brandAlert({ title: 'Something went wrong', message: 'Error deleting client. Please try again.', variant: 'error' })
    }
  }

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  if (loading) {
    return (
      <div className="panel-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gold"></div>
      </div>
    )
  }

  return (
    <div className="panel-page">
      <div className="panel-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="mb-4 flex items-center text-gray-600 hover:text-ink transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return to Admin Dashboard
              </button>
              <p className="eyebrow mb-3">Admin</p>
              <h1 className="panel-title flex items-center">
                <Users className="mr-3 h-8 w-8" />
                Client Management
              </h1>
              <p className="mt-2 text-gray-600">
                Manage all client accounts and their information
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowAddClientModal(true)}
                className="bg-ink text-white px-4 py-2 rounded-lg hover:bg-ink-soft transition-colors flex items-center"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Client
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Clients List */}
          <div className="lg:col-span-2">
            <GlassCard className="p-6">
              {/* Search and Filter */}
              <div className="mb-6 space-y-4 sm:space-y-0 sm:flex sm:items-center sm:space-x-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
                >
                  <option value="all">All Clients</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Clients List */}
              <div className="space-y-4">
                {filteredClients.map((client, index) => (
                  <motion.div
                    key={client.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    onClick={() => handleClientSelect(client)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                          {client.avatar_url ? (
                            <img
                              src={client.avatar_url}
                              alt={client.full_name}
                              className="h-12 w-12 rounded-full object-cover"
                            />
                          ) : (
                            <Users className="h-6 w-6 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-ink">
                            {client.full_name || 'Unnamed User'}
                          </h3>
                          <p className="text-sm text-gray-600">{client.email}</p>
                          <p className="text-xs text-gray-400">
                            Joined {new Date(client.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => handleSendGuide(e, client)}
                          disabled={sendingGuideFor === client.id}
                          className="p-2 text-gray-400 hover:text-brand transition-colors disabled:opacity-50"
                          title="Email Client Welcome Guide"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleEditClient(e, client)}
                          className="p-2 text-gray-400 hover:text-brand transition-colors"
                          title="Edit Client"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteClient(e, client)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete Client"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </GlassCard>
          </div>

          {/* Client Details */}
          <div className="lg:col-span-1">
            <GlassCard className="p-6">
              {selectedClient ? (
                <div>
                  <h3 className="font-display text-lg font-semibold text-ink mb-4">Client Details</h3>
                  
                  {/* Client Info */}
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-gray-300 mx-auto mb-3 flex items-center justify-center">
                        {selectedClient.avatar_url ? (
                          <img
                            src={selectedClient.avatar_url}
                            alt={selectedClient.full_name}
                            className="h-16 w-16 rounded-full object-cover"
                          />
                        ) : (
                          <Users className="h-8 w-8 text-gray-600" />
                        )}
                      </div>
                      <h4 className="font-semibold text-ink">
                        {selectedClient.full_name || 'Unnamed User'}
                      </h4>
                      <p className="text-sm text-gray-600">{selectedClient.email}</p>
                    </div>

                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Phone:</span>
                        <span className="ml-2 text-gray-600">{selectedClient.phone || 'Not provided'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Location:</span>
                        <span className="ml-2 text-gray-600">{selectedClient.location || 'Not provided'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Workout Time:</span>
                        <span className="ml-2 text-gray-600">{selectedClient.workout_time || 'Not set'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Fitness Goals:</span>
                        <div className="mt-1">
                          {selectedClient.fitness_goals && selectedClient.fitness_goals.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {selectedClient.fitness_goals.map((goal, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                >
                                  {goal}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-600">No goals set</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Client Packages */}
                    <div className="mt-6">
                      <h5 className="font-semibold text-ink mb-3">Active Packages</h5>
                      {clientPackages.length > 0 ? (
                        <div className="space-y-2">
                          {clientPackages.map((pkg) => (
                            <div key={pkg.id} className="p-3 bg-cream rounded-2xl">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-ink">{pkg.package_name}</span>
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  pkg.status === 'active' ? 'bg-green-100 text-green-800' :
                                  pkg.status === 'expired' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {pkg.status}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600 mt-1">
                                Expires: {new Date(pkg.expiry_date).toLocaleDateString()}
                              </p>
                              {pkg.sessions_remaining && (
                                <p className="text-xs text-gray-600">
                                  Sessions remaining: {pkg.sessions_remaining}
                                </p>
                              )}

                              {pkg.status === 'active' && trackerEdits[pkg.id] && (
                                <div className="mt-3 pt-3 border-t border-gold/20 space-y-2">
                                  <label className="block text-xs font-semibold text-gray-700">
                                    Progress Tracker link (Google Sheet)
                                  </label>
                                  <input
                                    type="url"
                                    value={trackerEdits[pkg.id].trackerUrl}
                                    onChange={(e) =>
                                      setTrackerEdits((prev) => ({
                                        ...prev,
                                        [pkg.id]: { ...prev[pkg.id], trackerUrl: e.target.value },
                                      }))
                                    }
                                    placeholder="https://docs.google.com/spreadsheets/..."
                                    className="w-full text-xs rounded-lg border border-gray-200 px-2 py-1.5"
                                  />

                                  <div className="flex items-center gap-3 flex-wrap">
                                    <label className="text-xs text-gray-700 flex items-center gap-1">
                                      Tracker type:
                                      <select
                                        value={trackerEdits[pkg.id].trackerType}
                                        onChange={(e) =>
                                          setTrackerEdits((prev) => ({
                                            ...prev,
                                            [pkg.id]: { ...prev[pkg.id], trackerType: e.target.value },
                                          }))
                                        }
                                        className="text-xs rounded-lg border border-gray-200 pl-1.5 pr-6 py-1 min-w-[108px]"
                                      >
                                        <option value="standard">Standard</option>
                                        <option value="competition">Competition</option>
                                      </select>
                                    </label>
                                  </div>

                                  <div className="flex items-center gap-4 flex-wrap">
                                    <label className="text-xs text-gray-700 flex items-center gap-1.5">
                                      <input
                                        type="checkbox"
                                        checked={trackerEdits[pkg.id].includesTraining}
                                        onChange={(e) =>
                                          setTrackerEdits((prev) => ({
                                            ...prev,
                                            [pkg.id]: { ...prev[pkg.id], includesTraining: e.target.checked },
                                          }))
                                        }
                                      />
                                      Training Guide unlocked
                                    </label>
                                    <label className="text-xs text-gray-700 flex items-center gap-1.5">
                                      <input
                                        type="checkbox"
                                        checked={trackerEdits[pkg.id].competitionPrep}
                                        onChange={(e) =>
                                          setTrackerEdits((prev) => ({
                                            ...prev,
                                            [pkg.id]: { ...prev[pkg.id], competitionPrep: e.target.checked },
                                          }))
                                        }
                                      />
                                      Competition Prep unlocked
                                    </label>
                                  </div>

                                  <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                      onClick={() => handleSaveTracker(pkg.id)}
                                      disabled={savingTrackerFor === pkg.id}
                                      className="text-xs font-semibold text-white bg-ink rounded-lg px-3 py-1.5 hover:bg-ink-soft transition-colors disabled:opacity-50"
                                    >
                                      {savingTrackerFor === pkg.id ? 'Saving…' : 'Save tracker & guides'}
                                    </button>

                                    {pkg.competition_prep && (
                                      <button
                                        onClick={() => handleSendCompetitionPrep(pkg.id)}
                                        disabled={sendingCompPrepFor === pkg.id}
                                        title="Send this client the Competition Prep: Final 4 Weeks & Peak Week Guide now"
                                        className="text-xs font-semibold text-ink bg-gold-soft border border-gold/40 rounded-lg px-3 py-1.5 hover:bg-gold/10 transition-colors disabled:opacity-50"
                                      >
                                        {sendingCompPrepFor === pkg.id ? 'Sending…' : 'Send Competition Prep Guide'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-600 text-sm">No active packages</p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-6 space-y-2">
                      <button 
                        onClick={() => navigate('/admin/sessions')}
                        className="w-full bg-ink text-white py-2 px-4 rounded-lg hover:bg-ink-soft transition-colors flex items-center justify-center"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        Schedule Session
                      </button>
                      <button
                        onClick={openAssignPackageModal}
                        className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                      >
                        <Package className="mr-2 h-4 w-4" />
                        {(() => {
                          const mealPlanCount = availablePackages.filter(pkg => pkg.package_type === 'meal_plan').length
                          const totalPackages = availablePackages.length
                          if (mealPlanCount === totalPackages && mealPlanCount > 0) {
                            return 'Assign Meal Plan'
                          } else if (mealPlanCount > 0) {
                            return 'Assign Package/Meal Plan'
                          } else {
                            return 'Assign Package'
                          }
                        })()}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a client to view details</p>
                </div>
              )}
            </GlassCard>
          </div>
        </div>

        {/* Add Client Modal */}
        {showAddClientModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-ink mb-4">Add New Client</h2>
                
                <form onSubmit={handleAddClient} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      placeholder="Enter client's full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      placeholder="Enter client's email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      placeholder="Enter client's phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      placeholder="Enter client's location"
                    />
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddClientModal(false)}
                      className="btn-secondary text-sm px-5 py-2.5"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-gold text-sm px-5 py-2.5 rounded-full hover:bg-ink-soft transition-colors"
                    >
                      Add Client
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Session Modal removed - now redirects to AdminSessions page */}

        {/* Assign Package Modal */}
        {showAssignPackageModal && selectedClient && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-start justify-between gap-4 mb-1">
                <h2 className="text-xl font-bold">
                  Assign a package to {selectedClient.full_name || selectedClient.email}
                </h2>
                <span className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-400 whitespace-nowrap">
                  Step {assignStep} of 2
                </span>
              </div>

              {assignStep === 1 ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">Pick the program they signed up for.</p>

                  {availablePackages.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center">
                      <p className="text-sm text-gray-600">
                        There are no packages set up yet, so there is nothing to choose from.
                      </p>
                      <button
                        type="button"
                        onClick={addWebsitePackages}
                        disabled={seedingPackages}
                        className="mt-4 inline-flex items-center px-4 py-2 rounded-lg bg-ink text-white hover:bg-ink-soft transition-colors disabled:opacity-50"
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        {seedingPackages ? 'Adding…' : 'Add my website programs'}
                      </button>
                      <p className="mt-3 text-xs text-gray-500">
                        Adds The Strong Again Method (private and online), Champion&apos;s Blueprint,
                        The Pro Look, Elite Competitive Edge and the Nutrition Blueprint meal plan.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {PACKAGE_GROUPS.map((group) => {
                        const groupPackages = availablePackages.filter(
                          (pkg) => pkg.package_type === group.type
                        )
                        if (groupPackages.length === 0) return null
                        return (
                          <div key={group.type}>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                              {group.label}
                            </p>
                            <div className="space-y-2">
                              {groupPackages.map((pkg) => (
                                <button
                                  key={pkg.id}
                                  type="button"
                                  onClick={() => chooseAssignPackage(pkg)}
                                  className="w-full flex items-center gap-3 text-left p-3 rounded-xl border border-gray-200 hover:border-green-500 hover:bg-green-50 transition-colors"
                                >
                                  <span className="flex-1 min-w-0">
                                    <span className="block font-medium text-ink">{pkg.name}</span>
                                    {pkg.description && (
                                      <span className="block text-xs text-gray-500 truncate">
                                        {pkg.description}
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-sm font-semibold text-ink whitespace-nowrap">
                                    ${pkg.price}
                                  </span>
                                  <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                </button>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setShowAssignPackageModal(false)}
                    className="w-full btn-secondary text-sm py-2.5 mt-6"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <form onSubmit={handleAssignPackage}>
                  <button
                    type="button"
                    onClick={() => setAssignStep(1)}
                    className="inline-flex items-center text-sm text-gray-600 hover:text-ink mb-5"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {assignPackage?.name}
                  </button>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        How long is this package?
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {assignTerms.map((term, index) => (
                          <button
                            key={`${term.label}-${index}`}
                            type="button"
                            onClick={() => setAssignTermIndex(index)}
                            className={`text-left p-3 rounded-xl border transition-colors ${
                              assignTermIndex === index
                                ? 'border-green-600 bg-green-50'
                                : 'border-gray-200 hover:border-gray-400'
                            }`}
                          >
                            <span className="block text-sm font-medium text-ink">{term.label}</span>
                            {term.price && (
                              <span className="block text-xs text-gray-600">{term.price}</span>
                            )}
                            {term.detail && (
                              <span className="block text-xs text-gray-500">{term.detail}</span>
                            )}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setAssignTermIndex(CUSTOM_TERM)}
                          className={`text-left p-3 rounded-xl border transition-colors ${
                            assignTermIndex === CUSTOM_TERM
                              ? 'border-green-600 bg-green-50'
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          <span className="block text-sm font-medium text-ink">Custom length</span>
                          <span className="block text-xs text-gray-500">Set your own</span>
                        </button>
                      </div>
                      {assignTermIndex === CUSTOM_TERM && (
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            value={assignCustomLength}
                            onChange={(e) => setAssignCustomLength(e.target.value)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <select
                            value={assignCustomUnit}
                            onChange={(e) => setAssignCustomUnit(e.target.value as 'week' | 'month')}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          >
                            <option value="week">weeks</option>
                            <option value="month">months</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        How many sessions?
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={assignSessions}
                        onChange={(e) => setAssignSessions(e.target.value)}
                        placeholder="e.g. 12"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Shows on their account as sessions remaining. Leave blank if this package
                        isn&apos;t sold by the session.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <DatePicker
                        value={packageAssignmentDate}
                        onChange={setPackageAssignmentDate}
                        placeholder="mm/dd/yyyy"
                        className="w-full"
                      />
                    </div>

                    {assignStartDate && assignEndDate && activeTerm && (
                      <div className="rounded-xl bg-cream p-3 text-sm text-gray-700">
                        Runs <span className="font-medium">{assignStartDate.toLocaleDateString()}</span>
                        {' through '}
                        <span className="font-medium">{assignEndDate.toLocaleDateString()}</span>
                        {` · ${formatTermLength(activeTerm)}`}
                        {assignSessions.trim() !== '' && ` · ${assignSessions.trim()} sessions`}
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowAssignPackageModal(false)}
                      className="flex-1 btn-secondary text-sm py-2.5"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={assigningPackage}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {assigningPackage ? 'Assigning…' : 'Assign Package'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Edit Client Modal */}
        {showEditModal && selectedClient && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Edit Client: {selectedClient.full_name}</h2>
                <form onSubmit={async (e) => {
                  e.preventDefault()
                  try {
                    // Prepare update data, excluding empty date_of_birth
                    const updateData: any = {
                      full_name: formData.full_name,
                      email: formData.email,
                      phone: formData.phone || null,
                      location: formData.location || null,
                      fitness_goals: formData.fitness_goals,
                      workout_time: formData.workout_time || null,
                      updated_at: new Date().toISOString()
                    }

                    // Only include date_of_birth if it's not empty
                    if (formData.date_of_birth && formData.date_of_birth.trim() !== '') {
                      updateData.date_of_birth = formData.date_of_birth
                    }

                    await upsertPreference(selectedClient.id, {
                      email: formData.email,
                      fullName: formData.full_name,
                      phone: formData.phone || undefined,
                      location: formData.location || undefined,
                      dateOfBirth: updateData.date_of_birth,
                      fitnessGoals: formData.fitness_goals,
                      workoutTime: formData.workout_time || undefined,
                    })

                    // Update local state
                    const updatedClient = {
                      ...selectedClient,
                      full_name: formData.full_name,
                      email: formData.email,
                      phone: formData.phone,
                      location: formData.location,
                      date_of_birth: formData.date_of_birth,
                      fitness_goals: formData.fitness_goals,
                      workout_time: formData.workout_time
                    }
                    
                    setClients(clients.map(client => 
                      client.id === selectedClient.id ? updatedClient : client
                    ))
                    setSelectedClient(updatedClient)
                    setShowEditModal(false)
                    void brandAlert({ title: 'Done', message: 'Client updated successfully!', variant: 'success' })
                  } catch (error) {
                    console.error('Error updating client:', error)
                    void brandAlert({ title: 'Something went wrong', message: 'Error updating client. Please try again.', variant: 'error' })
                  }
                }}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <input
                        type="text"
                        required
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                      <DatePicker
                        value={formData.date_of_birth}
                        onChange={(date) => setFormData({...formData, date_of_birth: date})}
                        placeholder="mm/dd/yyyy"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Workout Time</label>
                      <input
                        type="text"
                        value={formData.workout_time}
                        onChange={(e) => setFormData({...formData, workout_time: e.target.value})}
                        placeholder="e.g., Morning, Evening"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 btn-secondary text-sm py-2.5"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 btn-gold text-sm px-5 py-2.5 rounded-full hover:bg-ink-soft"
                    >
                      Update Client
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default AdminClients 